import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CONFIG } from "../config";

const BASE = CONFIG.price; // accurate reference price ($0.5649)
const BAND = 0.03; // random walk stays within +/-3% of base
const STEP = 0.0045; // per-second max movement
const RESYNC_EVERY = 20; // snap back to accurate price every 20s

// Shared price-tone constants so all price displays stay consistent.
export const PRICE_UP = "#7FB894";
export const PRICE_DOWN = "#C08F8F";
export const PRICE_NEUTRAL = "#F1F2EF";

// Map a tick direction (-1 / 0 / 1) to a color. Replaces nested ternaries.
export function directionColor(direction, neutral = PRICE_NEUTRAL) {
  if (direction > 0) return PRICE_UP;
  if (direction < 0) return PRICE_DOWN;
  return neutral;
}

// Direction of a value change relative to a previous value.
function changeSign(next, prev) {
  if (next > prev) return 1;
  if (next < prev) return -1;
  return 0;
}

const LivePriceContext = createContext({ price: BASE, base: BASE, direction: 0 });

export function LivePriceProvider({ children }) {
  const [price, setPrice] = useState(BASE);
  const [direction, setDirection] = useState(0); // -1, 0, 1 for tick color
  const tickRef = useRef(0);

  useEffect(() => {
    const advancePrice = (prev) => {
      // Every RESYNC_EVERY seconds, adjust back to the correct price.
      if (tickRef.current % RESYNC_EVERY === 0) {
        setDirection(changeSign(BASE, prev));
        return Number(BASE.toFixed(4));
      }

      const delta = (Math.random() - 0.5) * 2 * STEP;
      let next = prev + delta;
      const min = BASE * (1 - BAND);
      const max = BASE * (1 + BAND);
      if (next < min) next = min + Math.random() * STEP;
      if (next > max) next = max - Math.random() * STEP;

      setDirection(changeSign(next, prev));
      return Number(next.toFixed(4));
    };

    const id = setInterval(() => {
      tickRef.current += 1;
      setPrice(advancePrice);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Memoized context value to avoid re-creating the object on every render.
  const value = useMemo(
    () => ({ price, base: BASE, direction }),
    [price, direction]
  );

  return (
    <LivePriceContext.Provider value={value}>
      {children}
    </LivePriceContext.Provider>
  );
}

export function useLivePrice() {
  return useContext(LivePriceContext);
}
